// Modelo declarativo del menú principal.
//
// SPEC_VISIBILIDAD_MODULOS_POR_ROL_v0.1.md · PLAN 1.2 y 3
//
// **Por qué este archivo existe.** Hasta acá la relación «una sección se ve si
// alguno de sus ítems se ve» (RV-2) vivía sólo en la cabeza de quien escribía
// el template de `AppNavbar.vue`. No se sostuvo: el desplegable «Operación»
// era el único de nueve **sin `v-if`** y se abría vacío para cuatro roles
// (A-2), y siete de los ocho ítems de «Admin.» tampoco tenían gate (A-12).
// Ninguno de los dos se nota leyendo un template de mil líneas.
//
// Declarando el menú como dato, la visibilidad de una sección **se calcula** a
// partir de la de sus ítems, así que una sección vacía deja de ser posible por
// construcción en vez de por disciplina.
//
// **`.mjs` y no `.js`**: `package.json` no declara `"type": "module"`, así que
// Node lee un `.js` como CommonJS y el test puro no podría importarlo. Mismo
// motivo por el que ya existen `utils/money.mjs`, `cashPayments.mjs` y
// `whatsapp.mjs` — los tres son exactamente los módulos que las pruebas puras
// importan. Webpack maneja las dos formas; quien impone la extensión es Node.
//
// **Este archivo NO decide permisos.** Decide qué se OFRECE. Es la regla RV-1:
// que el cajero pueda *leer* un producto para cobrarlo (D25) no significa que
// se le ofrezca *administrar* el catálogo. Para sacar algo de un menú nunca se
// revoca un permiso.

// Módulos deliberadamente fuera del menú (D24: se ocultan, NO se desactivan —
// sus rutas y permisos siguen vivos en `router/index.js`).
//
// Se declaran acá y no con una bandera dentro de `MENU` a propósito: un módulo
// oculto que sigue declarado como sección es una invitación a «reactivarlo»
// sin leer por qué se ocultó. El test de coherencia usa esta lista para
// tolerar las ausencias del template, así que la excepción es dato y no una
// rama del test.
export const OCULTOS = [
  { href: '/ecommerce', motivo: 'No hay storefront: `ecommerce-graciela` no se copió (AGENTS.md 0.1). D-R6' },
  { href: '/mantenimiento/balanzas', motivo: 'Integración con balanza de despensa; un colegio no pesa uniformes. D-R6' },
];

export const MENU = [
  {
    id: 'suscripciones',
    hook: 'menu-suscripciones',
    items: [
      // Sin `v-if` en el template de hoy: heredaba el gate de la sección, que
      // era el mismo permiso. Se declara explícito para que el ítem no dependa
      // de que la sección siga preguntando lo mismo.
      { href: '/suscripciones', permisos: ['ver_suscripciones'] },
      { href: '/suscripciones/lote', permisos: ['administrar_suscripciones'] },
      { href: '/suscripciones/esquemas', permisos: ['ver_esquemas_cobro'] },
      { href: '/suscripciones/generacion', permisos: ['generar_deudas'] },
      // D38: el único ítem del módulo que `gerente` ve. Su permiso propio es
      // lo que hace que verlo NO implique ver esquemas ni generación.
      { href: '/suscripciones/cargos', permisos: ['administrar_cargos_eventuales'] },
    ],
  },
  {
    // A-2: hoy es el único `<li>` sin `v-if` de los nueve. Acá no puede no
    // tenerlo — la sección se deriva de sus ítems.
    id: 'operacion',
    hook: 'menu-operacion',
    items: [
      { href: '/cobranza', permisos: ['cobrar_deudas'] },
      { href: '/cobranza/estado-cuenta', permisos: ['ver_estado_cuenta'] },
      { href: '/cobranza/cobros', permisos: ['ver_cobros'] },
      { href: '/cobranza/pendientes-facturar', permisos: ['ver_cobros'] },
      { href: '/caja', permisos: ['abrir_caja', 'ver_caja'] },
      { href: '/movimientos-caja', permisos: ['ver_caja'] },
      { href: '/cerrar-caja', permisos: ['cerrar_caja'] },
      { href: '/ventas-facturas', permisos: ['ver_ventas'] },
      { href: '/cobro-factura-venta', permisos: ['ver_cobros_pendientes', 'cobrar_venta'] },
      // SPEC_NOTAS_DE_CREDITO D44: era «Devoluciones», sólo con `cobrar_venta`.
      // Ver y entregar una NC alcanza con `ver_cobros` (RN-144); devolver
      // mercadería, adentro de la pantalla, sigue exigiendo `cobrar_venta`.
      { href: '/notas-credito', permisos: ['ver_cobros', 'cobrar_venta'] },
    ],
  },
  {
    // La ÚNICA sección con `entrada`, y vale explicar por qué.
    //
    // Derivarla de sus ítems reintroduce A-3: `caja` tiene `ver_productos` y
    // `ver_precio_venta` —los necesita para cobrar un uniforme (D25)— que son
    // los permisos de «Productos» y «Precios». Con derivación pura el cajero
    // vuelve a ver el módulo entero.
    //
    // Subirle el permiso a los ítems tampoco sirve: `gerente` tampoco tiene
    // `editar_productos` y es un rol que sí debe consultar el catálogo.
    //
    // Lo que separa a `caja` de `gerente` no es ningún permiso de los ítems
    // —los dos pueden ver un producto—: es que `gerente` tiene además interés
    // administrativo en el inventario. Eso es lo que pregunta `entrada`.
    id: 'inventario',
    hook: 'menu-inventario',
    entrada: [
      'crear_productos', 'editar_productos', 'crear_precio_venta',
      'ver_depositos', 'gestionar_alertas', 'gestionar_politicas',
      'control_inventario_listar_control', 'control_inventario_cargar',
      'control_inventario_generar',
    ],
    items: [
      { href: '/inventario', permisos: ['gestionar_alertas'] },
      { href: '/inventario/productos', permisos: ['ver_productos'] },
      { href: '/inventario/productos-precio', permisos: ['ver_precio_venta'] },
      { href: '/inventario/grupos-precio', permisos: ['ver_precio_venta'] },
      { href: '/inventario/presentaciones', permisos: ['ver_productos'] },
      { href: '/inventario/categorias', permisos: ['ver_categorias'] },
      { href: '/inventario/unidades-medida', permisos: ['ver_unidad_medida'] },
      { href: '/inventario/movimientos', permisos: ['ver_depositos'] },
      // Sin `v-if` en el template de hoy (A-12, menor).
      { href: '/inventario/lotes', permisos: ['ver_depositos'] },
      { href: '/inventario/depositos', permisos: ['ver_depositos'] },
      {
        href: '/inventario/control',
        permisos: ['control_inventario_listar_control', 'control_inventario_cargar', 'control_inventario_generar'],
      },
    ],
  },
  {
    id: 'compras',
    hook: 'menu-compras',
    items: [
      { href: '/listar-proveedores', permisos: ['ver_proveedor'] },
      { href: '/listar-notas-remision', permisos: ['ver_nota_remision'] },
      { href: '/listar-comprobantes', permisos: ['ver_factura_compra'] },
    ],
  },
  {
    // AMB-V1: «Créditos» y «Cobro cliente crédito» SIGUEN en el menú. Lo que
    // se corrige es adentro de la pantalla (A-11): la escritura de créditos es
    // de gerencia, y el seed ya lo reparte así — `caja` tiene `ver_credito` y
    // `cobrar_deuda`, y ninguna de las tres de escritura.
    id: 'clientes',
    hook: 'menu-clientes',
    items: [
      { href: '/clientes', permisos: ['ver_cliente'] },
      { href: '/clientes/creditos', permisos: ['ver_credito'] },
      { href: '/cobro-cliente-credito', permisos: ['cobrar_deuda'] },
    ],
  },
  {
    // A-5, A-9 y A-12 se cierran acá de un solo movimiento.
    //
    // Hoy la sección se gatea con `actualizar_deposito || ver_depositos`
    // —permisos de DEPÓSITO— aunque contiene lo fiscal y los jobs. Consecuencia
    // medida: `soporte_ventax`, cuyo trabajo por D14 es justamente la
    // configuración fiscal, NO tiene ninguno de los dos y por lo tanto no tiene
    // camino de menú a su propia tarea; llegaba tipeando la URL, y sólo porque
    // esas rutas tampoco estaban gateadas (A-6). Y al revés: `ayudante_deposito`
    // tiene `ver_depositos` y hoy ve el menú fiscal completo.
    //
    // Derivada de sus ítems, la sección da exactamente el gate correcto: no
    // necesita `entrada`.
    id: 'administracion',
    hook: 'menu-administracion',
    items: [
      {
        href: '/mantenimiento',
        permisos: [
          'ver_depositos', 'actualizar_deposito', 'ver_facturacion_electronica',
          'gestionar_facturacion_electronica', 'gestionar_jobs',
        ],
      },
      { href: '/mantenimiento/operativo', permisos: ['ver_depositos'] },
      { href: '/mantenimiento/fiscal', permisos: ['ver_facturacion_electronica'] },
      { href: '/mantenimiento/fiscal/points', permisos: ['ver_facturacion_electronica'] },
      { href: '/mantenimiento/fiscal/timbrados', permisos: ['ver_facturacion_electronica'] },
      { href: '/mantenimiento/fiscal/series', permisos: ['ver_facturacion_electronica'] },
      { href: '/mantenimiento/cajas', permisos: ['actualizar_deposito'] },
    ],
  },
  {
    id: 'seguridad',
    hook: 'menu-seguridad',
    items: [
      { href: '/user-management', permisos: ['ver_usuarios'] },
      { href: '/roles', permisos: ['ver_roles'] },
      { href: '/permisos', permisos: ['ver_permisos'] },
    ],
  },
  {
    // Sin dropdown: un solo ítem. Se modela igual que las demás para que la
    // regla de visibilidad sea una sola.
    id: 'reportes',
    hook: 'menu-reportes',
    items: [
      { href: '/reportes', permisos: ['ver_reportes'] },
    ],
  },
];

/** Un ítem se ve si el usuario tiene ALGUNO de sus permisos. */
export const itemVisible = (item, tiene) => item.permisos.some(tiene);

/**
 * RV-2, escrita una sola vez y en un solo lugar.
 *
 * El `&& items.some(...)` NO es opcional: es lo que impide que una sección se
 * renderice vacía. `entrada` sólo puede RESTRINGIR — puede esconder de más,
 * nunca mostrar de más, que es el lado seguro del error.
 */
export const seccionVisible = (seccion, tiene) =>
  (!seccion.entrada || seccion.entrada.some(tiene)) &&
  seccion.items.some((item) => itemVisible(item, tiene));

/** Ids de las secciones visibles, para que el template pregunte una sola cosa. */
export const seccionesVisibles = (tiene) =>
  new Set(MENU.filter((seccion) => seccionVisible(seccion, tiene)).map((s) => s.id));
